import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/v3-dashboard/components/UI/Modal';
import { useUpgradeCheckout } from '@/hooks/useUpgradeCheckout';
import {
  getFeatureNotAvailableTitleAdvanced,
  getFeatureSubtitleAdvancedToPro,
} from '@/helpers/tier/featureTitles';
import { getAddonConfigForFeature, PLAN_AND_ADDON_SUBHEADER } from './addonConfigs';
import ProPlanIcon from '@/shared/icons/ProPlanIcon';
import Button from '@/shared/components/UI/Button';
import useDanteApi from '@/hooks/useDanteApi';
import * as plansService from '@/services/plans.service';
import PlanInfoBox from './PlanInfoBox';
import FeaturesList from './FeaturesList';
import AddonSection from './AddonSection';
import mixpanel from '@/utils/mixpanel';
import useModalStore from '@/stores/modal/modalStore';
import useTrackModalViewed from '@/hooks/useTrackModalViewed';
import { getTierFeaturesConfig, FEATURE_KEY_TO_LABEL } from '@/v3-dashboard/pages/Billing/tierFeatures';

function getProPricing(tiers) {
  const names = ['pro', 'professional', 'ultra'];
  const monthly = tiers?.monthly?.find((t) =>
    names.includes(String(t.name).toLowerCase())
  );
  const yearly = tiers?.yearly?.find((t) =>
    names.includes(String(t.name).toLowerCase())
  );
  return {
    monthly: monthly?.price ?? 400,
    yearly: yearly?.price ?? 4000,
    yearlyPerMonth: yearly ? Math.round(yearly.price / 12) : 333,
  };
}

const AdvancedToProModal = ({
  isOpen,
  onClose,
  feature,
  currentFeatureTitle,
  currentTier,
  targetTier,
}) => {
  const navigate = useNavigate();
  const { startCheckout, isLoading } = useUpgradeCheckout('pro');
  const { data: tiers } = useDanteApi(plansService.getTiers);

  const addonConfig = getAddonConfigForFeature(feature);
  const pricing = getProPricing(tiers);

  const title = getFeatureNotAvailableTitleAdvanced(feature);
  const subtitle = getFeatureSubtitleAdvancedToPro(feature);
  const proFeatures = getTierFeaturesConfig('pro').key_features;
  const highlightLabel = feature ? FEATURE_KEY_TO_LABEL[feature] : null;

  const modalStore = useModalStore();
  const commonTrackingProps = {
    version: 'pricing-modal-v2',
    modal_variant: modalStore.modalVariant,
    modal_type: addonConfig ? 'plan_and_addon' : 'plan_only',
    current_tier: currentTier,
    target_tier: targetTier,
    feature_name: feature || null,
    feature_title: currentFeatureTitle || null,
    source: modalStore.upgradeContext?.source || null,
    addon_shown: addonConfig?.addonKey || null,
    addon_label: addonConfig?.label || null,
    plan_shown: 'pro',
    plan_monthly_price: `$${pricing.monthly}`,
    plan_yearly_price: `$${pricing.yearly}`,
  };

  useTrackModalViewed(isOpen, modalStore.upgradeContext, commonTrackingProps);

  const handleDismiss = (method = 'close_button') => {
    mixpanel.track('Upgrade Path Modal Dismissed', {
      ...commonTrackingProps,
      dismiss_method: method,
    });
    onClose();
  };

  const handleSeeAllPlans = () => {
    mixpanel.track('Upgrade Path Modal See All Plans Clicked', commonTrackingProps);
    onClose();
    navigate('/plans');
  };

  const planUpgradeContent = (
    <>
      <PlanInfoBox
        icon={<ProPlanIcon className="text-v3-orange-primary" />}
        title="Pro plan"
        subtitle="For businesses wanting more advanced AI"
      />
      <FeaturesList
        features={proFeatures}
        maxFeatures={5}
        highlightFeature={highlightLabel}
      />
      <span className="font-inter-variable text-xs font-medium leading-4 text-v3-black-secondary">
        Only ${pricing.monthly}/mo or ${pricing.yearlyPerMonth}/mo if paid annually.
      </span>
    </>
  );

  const planUpgradeButton = (
    <Button
      variant="primary"
      size="md"
      label="Upgrade to Pro"
      onClick={() => {
        mixpanel.track('Upgrade Path Modal CTA Clicked', {
          ...commonTrackingProps,
          cta_label: 'Upgrade to Pro',
        });
        startCheckout();
      }}
      loading={isLoading}
      loadingLabel="Loading…"
      className="w-full justify-center"
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title={title}
      subtitle={addonConfig ? PLAN_AND_ADDON_SUBHEADER : subtitle}
      closeBackdrop
      className={addonConfig ? 'md:!w-[720px]' : 'md:!w-[550px]'}
    >
      {addonConfig ? (
        <div className="flex items-stretch w-full min-h-[320px]">
          <div className="flex flex-col items-start flex-1 min-w-0">
            <div className="flex flex-col items-start w-full gap-v3-lg">
              {planUpgradeContent}
            </div>
            <div className="flex flex-col items-stretch w-full mt-auto pt-v3-lg">
              {planUpgradeButton}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center shrink-0 px-v3-md">
            <div className="flex-1 min-h-[24px] w-px bg-v3-black-border" />
            <span className="font-lora italic text-v3-h10 text-v3-black-secondary py-v3-xs">OR</span>
            <div className="flex-1 min-h-[24px] w-px bg-v3-black-border" />
          </div>
          <AddonSection addonConfig={addonConfig} />
        </div>
      ) : (
        <div className="flex flex-col items-start w-full gap-v3-lg">
          {planUpgradeContent}
          {planUpgradeButton}
        </div>
      )}

      <div className="flex flex-col items-stretch gap-v3-xs w-full mt-v3-md">
        <Button
          variant="transparent"
          size="md"
          label="See all plans"
          onClick={handleSeeAllPlans}
          className="w-full justify-center"
        />
      </div>
    </Modal>
  );
};

export default AdvancedToProModal;
